import { useState } from "react";
import toast from "react-hot-toast";
import { Plus, X, Pencil, Package, ShoppingBag } from "lucide-react";
import { useSuppliers, useCreateSupplier, useUpdateSupplier, useSupplierBatches, SupplierRecord } from "@/api/inventory";
import { usePurchases } from "@/api/purchases";
import { getApiErrorMessage } from "@/api/client";

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

/**
 * Proveedores: el backend (inventory.service.ts) y los hooks del frontend
 * (useSuppliers/useCreateSupplier/useUpdateSupplier/useSupplierBatches) ya
 * existían desde hace tiempo — se usan en Compras (elegir proveedor) y en
 * Lotes (trazabilidad), pero no había ninguna pantalla para darlos de
 * alta o editarlos: la única forma de crear uno era directamente en la
 * base de datos. Esta página cierra ese hueco (alta/edición + activar o
 * desactivar) y agrega, por proveedor, su historial de compras y los
 * lotes que ha entregado (útil ante una alerta sanitaria: "¿qué otros
 * lotes nos trajo este proveedor?").
 */
export default function SuppliersPage() {
  const [showInactive, setShowInactive] = useState(false);
  const { data: suppliers, isLoading } = useSuppliers(showInactive ? undefined : true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<SupplierRecord | null>(null);
  const [viewing, setViewing] = useState<SupplierRecord | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Proveedores</h1>
          <p className="text-sm text-gray-500">Proveedores a los que se les compra mercancía — se usan al crear una orden de compra y para trazabilidad de lotes.</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
          <Plus size={16} className="mr-1.5" /> Nuevo proveedor
        </button>
      </div>

      <div className="card flex items-center gap-2 p-4">
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Mostrar también inactivos
        </label>
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Nombre</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">NIT / documento</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Teléfono</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Correo</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && (suppliers?.length ?? 0) === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                  Aún no hay proveedores registrados.
                </td>
              </tr>
            )}
            {suppliers?.map((s) => (
              <tr key={s.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-gray-900">
                  <button type="button" className="hover:underline" onClick={() => setViewing(s)}>
                    {s.name}
                  </button>
                </td>
                <td className="px-4 py-3 text-gray-600">{s.taxId ?? "—"}</td>
                <td className="px-4 py-3 text-gray-600">{s.phone ?? "—"}</td>
                <td className="px-4 py-3 text-gray-600">{s.email ?? "—"}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${s.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    {s.isActive ? "Activo" : "Inactivo"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button type="button" className="mr-3 text-xs font-medium text-brand-600 hover:text-brand-800" onClick={() => setViewing(s)}>
                    Ver historial
                  </button>
                  <button type="button" className="text-xs font-medium text-gray-600 hover:text-gray-900" onClick={() => setEditing(s)}>
                    <Pencil size={12} className="mr-1 inline" /> Editar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <SupplierFormModal onClose={() => setShowCreate(false)} />}
      {editing && <SupplierFormModal supplier={editing} onClose={() => setEditing(null)} />}
      {viewing && <SupplierDetailModal supplier={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function SupplierFormModal({ supplier, onClose }: { supplier?: SupplierRecord; onClose: () => void }) {
  const createSupplier = useCreateSupplier();
  const updateSupplier = useUpdateSupplier();
  const isEditing = !!supplier;

  const [name, setName] = useState(supplier?.name ?? "");
  const [taxId, setTaxId] = useState(supplier?.taxId ?? "");
  const [phone, setPhone] = useState(supplier?.phone ?? "");
  const [email, setEmail] = useState(supplier?.email ?? "");
  const [address, setAddress] = useState(supplier?.address ?? "");
  const [isActive, setIsActive] = useState(supplier?.isActive ?? true);

  const isPending = createSupplier.isPending || updateSupplier.isPending;

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("El nombre es obligatorio.");
      return;
    }
    try {
      const payload = { name, taxId: taxId || undefined, phone: phone || undefined, email: email || undefined, address: address || undefined };
      if (isEditing) {
        await updateSupplier.mutateAsync({ id: supplier.id, ...payload, isActive });
        toast.success("Proveedor actualizado.");
      } else {
        await createSupplier.mutateAsync(payload);
        toast.success("Proveedor registrado.");
      }
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">{isEditing ? "Editar proveedor" : "Nuevo proveedor"}</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-3 p-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Nombre</label>
            <input className="input-field mt-1" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">NIT / documento (opcional)</label>
            <input className="input-field mt-1" value={taxId} onChange={(e) => setTaxId(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Teléfono (opcional)</label>
            <input className="input-field mt-1" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Correo (opcional)</label>
            <input className="input-field mt-1" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Dirección (opcional)</label>
            <input className="input-field mt-1" value={address} onChange={(e) => setAddress(e.target.value)} />
          </div>
          {isEditing && (
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              Proveedor activo (aparece para elegir en Compras)
            </label>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" disabled={isPending} onClick={handleSubmit}>
            {isPending ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SupplierDetailModal({ supplier, onClose }: { supplier: SupplierRecord; onClose: () => void }) {
  const { data: purchasesResponse, isLoading: loadingPurchases } = usePurchases({ supplierId: supplier.id, pageSize: 10 });
  const { data: batches, isLoading: loadingBatches } = useSupplierBatches(supplier.id);
  const purchases = purchasesResponse?.data ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">{supplier.name}</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <div>
            <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
              <ShoppingBag size={13} /> Últimas compras
            </p>
            {loadingPurchases && <p className="text-sm text-gray-400">Cargando...</p>}
            {!loadingPurchases && purchases.length === 0 && <p className="text-sm text-gray-400">Sin compras registradas a este proveedor.</p>}
            <div className="divide-y divide-gray-100 rounded-md border border-gray-100">
              {purchases.map((p) => (
                <div key={p.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div>
                    <p className="text-gray-900">{p.purchaseDate}</p>
                    <p className="text-xs text-gray-400">{p.invoiceNumber ? `Factura ${p.invoiceNumber}` : "Sin # de factura"} · {p.status}</p>
                  </div>
                  <span className="font-medium text-gray-900">{currency.format(Number(p.total))}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
              <Package size={13} /> Lotes entregados
            </p>
            {loadingBatches && <p className="text-sm text-gray-400">Cargando...</p>}
            {!loadingBatches && (batches?.length ?? 0) === 0 && <p className="text-sm text-gray-400">Sin lotes registrados de este proveedor.</p>}
            <div className="divide-y divide-gray-100 rounded-md border border-gray-100">
              {batches?.map((b) => (
                <div key={b.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div>
                    <p className="text-gray-900">{b.product?.name ?? b.productId}</p>
                    <p className="text-xs text-gray-400">
                      {b.batchNumber ? `Lote ${b.batchNumber}` : "Sin # de lote"} · {b.quantity} u. · recibido {b.receivedAt}
                    </p>
                  </div>
                  {b.expirationDate && <span className="text-xs text-gray-500">Vence {b.expirationDate}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
